import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import { notifyUser } from "../utils/notifyUser";

const escapeCSVValue = (value) =>
    `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadCSV = (filename, headers, rows) => {
    const csv = [
        headers.map(escapeCSVValue).join(","),
        ...rows.map((row) => row.map(escapeCSVValue).join(",")),
    ].join("\n");
    const blob = new Blob([`\uFEFF${csv}`], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const DocumentCenter = () => {
    // Users tab state
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [docFile, setDocFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [modalDocs, setModalDocs] = useState([]);
    const [modalUser, setModalUser] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewedUserLoading, setViewedUserLoading] = useState(false);

    // Campaigns tab state
    const [activeTab, setActiveTab] = useState("users");
    const [campaigns, setCampaigns] = useState([]);
    const [, setCampaignDocs] = useState({});
    const [, setCampaignUploadLoading] = useState({});
    const [campaignDocFile, setCampaignDocFile] = useState({});
    const [campaignSearch, setCampaignSearch] = useState("");
    const [campaignPerPage, setCampaignPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [campaignCurrentPage, setCampaignCurrentPage] = useState(1);

    // Fetch users and user documents
    useEffect(() => {
        fetchUsers();
    }, []);

    // Fetch campaigns when campaigns tab is active
    useEffect(() => {
        if(activeTab !== "campaigns") return;
        const fetchCampaigns = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/public-campaigns`,
                    {
                        withCredentials: true,
                    },
                );
                setCampaigns(sortByAuditUpdatedOn(res.data));
            } catch{
                notifyUser(null, "error", "Error loading campaigns", "toast", null, null, null, null, null, false, false, "top-center");
            }
        };
        fetchCampaigns();
    }, [activeTab]);

    // Fetch campaign docs for a campaign
    const fetchCampaignDocs = async (campaignId) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/campaign/${campaignId}`,
                {
                    withCredentials: true,
                },
            );
            // Sort docs newest to oldest by audit_created_on
            const sortedDocs = [...res.data].sort((a, b) => {
                const dateA = new Date(a.audit_created_on || 0);
                const dateB = new Date(b.audit_created_on || 0);
                return dateB - dateA;
            });
            setCampaignDocs((prev) => ({ ...prev, [campaignId]: sortedDocs }));
        } catch{
            setCampaignDocs((prev) => ({ ...prev, [campaignId]: [] }));
        }
    };

    // Show campaign docs in modal
    const handleViewCampaignDocs = async (campaign) => {
        console.log("handleViewCampaignDocs called with:", campaign);
        if(!campaign || !campaign.id) {
            notifyUser(null, "error", "Invalid campaign object.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        // Fallback for missing first_name/last_name
        setModalUser({
            first_name: campaign.first_name || campaign.owner_first_name || "",
            last_name: campaign.last_name || campaign.owner_last_name || "",
            campaign_name: campaign.campaign_name || "",
        });
        setShowModal(true);
        console.log("setShowModal(true) called for campaign");
        setViewedUserLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/campaign/${campaign.id}`,
                {
                    withCredentials: true,
                },
            );
            const sortedDocs = [...res.data].sort((a, b) => {
                const dateA = new Date(a.audit_created_on || 0);
                const dateB = new Date(b.audit_created_on || 0);
                return dateB - dateA;
            });
            // newest first
            setModalDocs(sortedDocs);
        } catch(err) {
            setModalDocs([]);
            notifyUser(null, "error", "Failed to fetch campaign's documents", "toast", null, null, null, null, null, false, false, "top-center");
            console.error("Error fetching campaign documents:", err);
        }
        setViewedUserLoading(false);
    };

    // User tab handlers
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users`,
                {
                    withCredentials: true,
                },
            );
            const activeUsers = res.data.filter(
                (user) => user.is_deleted !== 1,
            );
            setUsers(sortByAuditUpdatedOn(activeUsers));
        } catch{
            notifyUser(null, "error", "Error loading users", "toast", null, null, null, null, null, false, false, "top-center");
        }
        setLoading(false);
    };
    const handleUserSelect = (userId) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId],);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        if(e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setDocFile(e.dataTransfer.files[0]);
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
    };
    const handleDocFileChange = (e) => {
        setDocFile(e.target.files[0]);
    };
    const handleDocUpload = async (e) => {
        e.preventDefault();
        if(!docFile || selectedUsers.length === 0) {
            notifyUser(null, "error", "Please select users and a document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        const formData = new FormData();
        formData.append("file", docFile);
        formData.append("userIds", JSON.stringify(selectedUsers));
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/upload`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            notifyUser(null, "success", "Document uploaded and assigned to users!", "toast", null, null, null, null, null, false, false, "top-center");
            setDocFile(null);
            setSelectedUsers([]);
        } catch{
            notifyUser(null, "error", "Failed to upload document", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };
    const handleViewUserDocs = async (user) => {
        if(!user || !user.id) {
            console.warn("handleViewUserDocs called with invalid user:", user);
            notifyUser(null, "error", "Invalid user selected.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        setModalUser(user);
        setShowModal(true);
        setViewedUserLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/user/${user.id}`,
                { withCredentials: true },
            );

            const sortedDocs = [...res.data].sort((a, b) => {
                const dateA = new Date(a.audit_created_on || 0);
                const dateB = new Date(b.audit_created_on || 0);
                return dateB - dateA;
            });
            setModalDocs(sortedDocs);
        } catch(err) {
            setModalDocs([]);
            console.error("Failed to fetch user's documents", err);
            notifyUser(null, "error", "Failed to fetch user's documents", "toast", null, null, null, null, null, false, false, "top-center");
        }
        setViewedUserLoading(false);
    };

    // Campaign tab handlers

    const handleCampaignDocUpload = async (e, campaignId) => {
        e.preventDefault();
        const file = campaignDocFile[campaignId] || campaignDocFile["global"];
        // Require at least one campaign selected and a file
        if(!file && (!selectedUsers || selectedUsers.length === 0)) {
            notifyUser(null, "error", "Please select at least one campaign and a document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        if(!file) {
            notifyUser(null, "error", "Please select a document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        if(!selectedUsers || selectedUsers.length === 0) {
            notifyUser(null, "error", "Please select at least one campaign.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        // Upload for each selected campaign
        for(const campId of selectedUsers) {
            setCampaignUploadLoading((prev) => ({ ...prev, [campId]: true }));
            const formData = new FormData();
            formData.append("file", file);
            try {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campId}/upload-document`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        withCredentials: true,
                    },
                );
                notifyUser(null, "success", `Document uploaded to campaign!`, "toast", null, null, null, null, null, false, false, "top-center");
                setCampaignDocFile((prev) => ({
                    ...prev,
                    [campId]: null,
                    global: null,
                }));
                fetchCampaignDocs(campId);
            } catch(err) {
                console.error(
                    "Failed to upload campaign document:",
                    err.response?.data || err,
                );
                notifyUser(null, "error", err.response?.data?.message ||
                        "Failed to upload campaign document", "toast", null, null, null, null, null, false, false, "top-center");
            }
            setCampaignUploadLoading((prev) => ({ ...prev, [campId]: false }));
        }
    };
    // Pagination and filtering for users
    const filteredUsers = users.filter((user) => {
        const matchesSearch =
            user.first_name?.toLowerCase().includes(search.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(search.toLowerCase()) ||
            user.email?.toLowerCase().includes(search.toLowerCase()) ||
            user.role?.toLowerCase().includes(search.toLowerCase());

        // Apply role filter
        const matchesRole =
            roleFilter === "all" ||
            user.role?.toLowerCase() === roleFilter.toLowerCase();

        return matchesSearch && matchesRole;
    });
    const totalPages = Math.ceil(filteredUsers.length / perPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    // Pagination and filtering for campaigns
    const filteredCampaigns = campaigns.filter(
        (c) =>
            c.campaign_name
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.company_name
                ?.toLowerCase()
                .includes(campaignSearch.toLowerCase()) ||
            c.email?.toLowerCase().includes(campaignSearch.toLowerCase()) ||
            c.owner_email?.toLowerCase().includes(campaignSearch.toLowerCase()),
    );
    const campaignTotalPages = Math.ceil(
        filteredCampaigns.length / campaignPerPage,
    );
    const paginatedCampaigns = filteredCampaigns.slice(
        (campaignCurrentPage - 1) * campaignPerPage,
        campaignCurrentPage * campaignPerPage,
    );

    const handleExportUsersCSV = () => {
        downloadCSV(
            "document-center-users.csv",
            ["First Name", "Last Name", "Email", "Role"],
            filteredUsers.map((user) => [
                user.first_name,
                user.last_name,
                user.email,
                user.role,
            ]),
        );
    };

    const handleExportCampaignsCSV = () => {
        downloadCSV(
            "document-center-campaigns.csv",
            ["Campaign Name", "Company Name", "Name", "Email"],
            filteredCampaigns.map((campaign) => [
                campaign.campaign_name,
                campaign.company_name,
                `${campaign.first_name || ""} ${campaign.last_name || ""}`.trim(),
                campaign.email || campaign.owner_email,
            ]),
        );
    };

    // DataTable columns
    const columns = [
        {
            name: "Select",
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={selectedUsers.includes(row.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleUserSelect(row.id)}
                />
            ),
            width: "80px",
            ignoreRowClick: true,
        },
        {
            name: "First Name",
            selector: (row) => row.first_name,
            sortable: true,
        },
        { name: "Last Name", selector: (row) => row.last_name, sortable: true },
        { name: "Email", selector: (row) => row.email, sortable: true },
        { name: "Role", selector: (row) => row.role, sortable: true },
    ];
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
        },
        table: { style: { borderRadius: "8px", overflowX: "hidden" } },
    };

    // Step guide animation state
    const [step, setStep] = useState(1);
    useEffect(() => {
        // Animate steps every 2.5s
        const steps = [1, 2, 3];
        let idx = 0;
        const interval = setInterval(() => {
            idx = (idx + 1) % steps.length;
            setStep(steps[idx]);
        }, 2500);
        return () => clearInterval(interval);
    }, [activeTab]);

    // Upload button disabled logic
    const isUserUploadDisabled = !(docFile && selectedUsers.length > 0);
    const isCampaignUploadDisabled = !(
        campaignDocFile["global"] && selectedUsers.length > 0
    );

    // Tooltip text
    const userTooltip =
        !docFile && selectedUsers.length === 0
            ? "Add a document and select one or more users."
            : !docFile
              ? "Add a document to the upload area."
              : selectedUsers.length === 0
                ? "Select one or more users."
                : "";
    const campaignTooltip =
        !campaignDocFile["global"] && selectedUsers.length === 0
            ? "Add a document and select one or more campaigns."
            : !campaignDocFile["global"]
              ? "Add a document to the upload area."
              : selectedUsers.length === 0
                ? "Select one or more campaigns."
                : "";

    return (
        <div className="font-poppins">
            <div
                className="aqi-grid-content p-8 w-full bg-white rounded shadow"
                style={{ maxWidth: "100%" }}
            >
                <h2
                    style={{
                        color: "var(--navy)",
                        marginBottom: "20px",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    DOCUMENTS CENTER
                </h2>
                {/* Step-by-step guide */}
                <div className="mb-6 flex flex-col items-center">
                    <div className="flex gap-6 items-center justify-center">
                        <div
                            className={`flex flex-col items-center transition-all duration-500 ${
                                step === 1
                                    ? "scale-110 text-navy"
                                    : "opacity-60"
                            }`}
                            style={{ minWidth: 120 }}
                        >
                            <span className="text-lg font-semibold">
                                1. Drag your documents
                            </span>
                            <span className="mt-1 animate-bounce">📄</span>
                        </div>
                        <div
                            className={`flex flex-col items-center transition-all duration-500 ${
                                step === 2
                                    ? "scale-110 text-navy"
                                    : "opacity-60"
                            }`}
                            style={{ minWidth: 120 }}
                        >
                            <span className="text-lg font-semibold">
                                2. Select one or more{" "}
                                {activeTab === "users"
                                    ? "users"
                                    : "campaigns"}
                            </span>
                            <span className="mt-1 animate-bounce">👤</span>
                        </div>
                        <div
                            className={`flex flex-col items-center transition-all duration-500 ${
                                step === 3
                                    ? "scale-110 text-navy"
                                    : "opacity-60"
                            }`}
                            style={{ minWidth: 120 }}
                        >
                            <span className="text-lg font-semibold">
                                3. Hit upload
                            </span>
                            <span className="mt-1 animate-bounce">⬆️</span>
                        </div>
                    </div>
                </div>
                {/* Tabs */}
                <div className="flex gap-4 mb-6">
                    <button
                        className={`px-4 py-2 rounded font-semibold transition ${
                            activeTab === "users"
                                ? "bg-navy text-white"
                                : "bg-gray-100 text-navy"
                        }`}
                        onClick={() => setActiveTab("users")}
                    >
                        Users
                    </button>
                    <button
                        className={`px-4 py-2 rounded font-semibold transition ${
                            activeTab === "campaigns"
                                ? "bg-navy text-white"
                                : "bg-gray-100 text-navy"
                        }`}
                        onClick={() => setActiveTab("campaigns")}
                    >
                        Campaigns
                    </button>
                </div>

                {/* Users Section */}
                {activeTab === "users" && (
                    <>
                        <form onSubmit={handleDocUpload} className="mb-6">
                            {/* ...user upload form... */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Select Document
                                </label>
                                <div
                                    className="document-center-dropzone w-full border rounded p-4 text-center bg-gray-50 hover:bg-gray-100 transition mb-2 relative"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    style={{
                                        border: "2px dashed #cbd5e1",
                                        position: "relative",
                                    }}
                                    onClick={() =>
                                        document
                                            .getElementById("doc-file-input")
                                            ?.click()
                                    }
                                >
                                    {docFile ? (
                                        <span className="flex items-center justify-center gap-2 text-navy font-semibold">
                                            {docFile.name}
                                            <button
                                                type="button"
                                                className="ml-2 text-xs text-red-600 underline hover:text-red-800"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDocFile(null);
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </span>
                                    ) : (
                                        <span className="text-gray-500">
                                            Drag & drop a file here, or click to
                                            select
                                        </span>
                                    )}
                                    <input
                                        id="doc-file-input"
                                        type="file"
                                        onChange={handleDocFileChange}
                                        className="w-full opacity-0 absolute left-0 top-0 h-full cursor-pointer"
                                        style={{
                                            position: "absolute",
                                            pointerEvents: "none",
                                        }}
                                        tabIndex={-1}
                                    />
                                </div>
                            </div>
                        </form>
                        <div className="aqi-grid-toolbar">
                                {/* Records per page dropdown */}
                                <div>
                                    <select
                                        style={{
                                            padding:
                                                "0.375rem 2rem 0.375rem 0.75rem",
                                            color: "var(--navy)",
                                            backgroundColor: "#F5F7FA",
                                            border: "2px solid #C2D7E2",
                                            borderRadius: "0.375rem",
                                            fontWeight: "500",
                                            appearance: "none",
                                            backgroundImage:
                                                "var(--navy-select-chevron)",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition:
                                                "right 0.5rem center",
                                            backgroundSize: "16px 12px",
                                            transition: "all 0.2s ease",
                                            cursor: "pointer",
                                            outline: "none",
                                        }}
                                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                        value={perPage}
                                        onChange={(e) => {
                                            setPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Role filter dropdown */}
                                <div>
                                    <select
                                        style={{
                                            padding:
                                                "0.375rem 2rem 0.375rem 0.75rem",
                                            color: "var(--navy)",
                                            backgroundColor: "#F5F7FA",
                                            border: "2px solid #C2D7E2",
                                            borderRadius: "0.375rem",
                                            fontWeight: "500",
                                            appearance: "none",
                                            backgroundImage:
                                                "var(--navy-select-chevron)",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition:
                                                "right 0.5rem center",
                                            backgroundSize: "16px 12px",
                                            transition: "all 0.2s ease",
                                            cursor: "pointer",
                                            outline: "none",
                                            minWidth: "180px",
                                        }}
                                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                        value={roleFilter}
                                        onChange={(e) => {
                                            setRoleFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">All Roles</option>
                                        <option value="investor">
                                            Investor
                                        </option>
                                        <option value="entrepreneur">
                                            Entrepreneur
                                        </option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>

                            <div className="aqi-grid-search">
                                <FaSearch
                                    className="absolute left-3 top-2.5"
                                    style={{ color: "var(--navy)" }}
                                />
                                <input
                                    type="text"
                                    placeholder="Search by name, email..."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    style={{
                                        paddingLeft: "2.5rem",
                                        paddingRight: "0.75rem",
                                        borderRadius: "0.375rem",
                                        border: "2px solid #C2D7E2",
                                        backgroundColor: "#F5F7FA",
                                        transition: "all 0.2s ease",
                                        outline: "none",
                                        color: "var(--navy)",
                                    }}
                                    className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm font-poppins"
                                />
                            </div>

                            <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                                <div className="relative group">
                                    <button
                                        type="button"
                                        onClick={handleDocUpload}
                                        className="aqi-grid-button aqi-grid-button--primary"
                                        disabled={isUserUploadDisabled}
                                    >
                                        Upload
                                    </button>
                                    {isUserUploadDisabled && (
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity duration-200">
                                            {userTooltip}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleExportUsersCSV}
                                    title="Export CSV"
                                    className="aqi-grid-button aqi-grid-button--primary"
                                >
                                    Export
                                </button>
                            </div>
                        </div>
                        <div
                            style={{
                                height: "56vh",
                                overflowY: "auto",
                                overflowX: "hidden",
                                borderRadius: "6px",
                                width: "100%",
                                display: "block",
                            }}
                        >
                            <DataTable
                                columns={columns}
                                data={paginatedUsers}
                                customStyles={{
                                    ...customStyles,
                                    table: {
                                        style: {
                                            borderRadius: "8px",
                                            minWidth: 0,
                                            width: "100%",
                                            tableLayout: "fixed",
                                            overflowX: "hidden",
                                        },
                                    },
                                    headRow: {
                                        style: {
                                            backgroundColor: "var(--navy)",
                                            color: "white",
                                            fontWeight: "bold",
                                            fontSize: "13px",
                                            minHeight: "50px",
                                            width: "100%",
                                        },
                                    },
                                    rows: {
                                        style: {
                                            cursor: "pointer",
                                            minHeight: "50px",
                                            width: "100%",
                                            "&:hover": {
                                                backgroundColor: "#E9F4F9",
                                                borderLeft: "2px solid var(--navy)",
                                            },
                                        },
                                    },
                                }}
                                progressPending={loading}
                                onRowClicked={handleViewUserDocs}
                                striped
                                pointerOnHover
                                noHeader
                                highlightOnHover
                            />
                        </div>
                        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                            <span>
                                Showing{" "}
                                {filteredUsers.length === 0
                                    ? 0
                                    : (currentPage - 1) * perPage + 1}{" "}
                                to{" "}
                                {Math.min(
                                    currentPage * perPage,
                                    filteredUsers.length,
                                )}{" "}
                                of {filteredUsers.length} users
                            </span>
                            <div className="flex gap-2 text-lg items-center">
                                <button
                                    disabled={currentPage === 1 || loading}
                                    onClick={() => setCurrentPage(1)}
                                    className="disabled:opacity-40"
                                >
                                    <FaAngleDoubleLeft />
                                </button>
                                <button
                                    disabled={currentPage === 1 || loading}
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.max(prev - 1, 1),)
                                    }
                                    className="disabled:opacity-40"
                                >
                                    <FaAngleLeft />
                                </button>
                                <span className="text-sm px-2">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    disabled={
                                        currentPage === totalPages || loading
                                    }
                                    onClick={() =>
                                        setCurrentPage((prev) =>
                                            Math.min(prev + 1, totalPages),)
                                    }
                                    className="disabled:opacity-40"
                                >
                                    <FaAngleRight />
                                </button>
                                <button
                                    disabled={
                                        currentPage === totalPages || loading
                                    }
                                    onClick={() => setCurrentPage(totalPages)}
                                    className="disabled:opacity-40"
                                >
                                    <FaAngleDoubleRight />
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* Modal for viewing user or campaign documents - always rendered when showModal is true */}
                {showModal && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-2">
                        <div className="bg-white rounded-2xl w-full max-w-2xl p-6 md:p-8 relative max-h-[90vh] overflow-y-auto font-poppins shadow-2xl">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() => {
                                    setShowModal(false);
                                    setModalDocs([]);
                                    setModalUser(null);
                                    setPreviewDoc(null);
                                }}
                            >
                                <FaTimes />
                            </button>
                            <h3 className="text-2xl font-bold mb-4 text-navy">
                                Documents for {modalUser?.first_name}{" "}
                                {modalUser?.last_name}
                                {modalUser?.campaign_name
                                    ? ` (${modalUser.campaign_name})`
                                    : ""}
                            </h3>
                            {viewedUserLoading ? (
                                <div>Loading documents...</div>
                            ) : Array.isArray(modalDocs) &&
                              modalDocs.length === 0 ? (
                                <div className="text-gray-500 italic">
                                    No uploads yet.
                                </div>
                            ) : (
                                <ul className="list-disc pl-5 space-y-2">
                                    {modalDocs &&
                                        modalDocs.map((doc, idx) => {
                                            const url = /^https?:\/\//i.test(doc.source_url || "")
                                                ? doc.source_url
                                                : `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.filename}`;
                                            const ext = doc.originalname
                                                .split(".")
                                                .pop()
                                                .toLowerCase();
                                            return (
                                                <li
                                                    key={idx}
                                                    className="flex items-center gap-2"
                                                >
                                                    <button
                                                        className="text-blue-600 underline hover:text-blue-800 text-left"
                                                        onClick={() =>
                                                            setPreviewDoc({
                                                                url,
                                                                ext,
                                                                name: doc.originalname,
                                                            })
                                                        }
                                                    >
                                                        {doc.originalname}
                                                    </button>
                                                    {doc.electronically_signed_at && (
                                                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                                                            Electronically signed
                                                            {doc.signature_text
                                                                ? ` by ${doc.signature_text}`
                                                                : ""}{" "}
                                                            {new Date(doc.electronically_signed_at).toLocaleString()}
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                </ul>
                            )}
                            {/* Preview modal for a document */}
                            {previewDoc && (
                                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
                                    <div className="bg-white p-6 rounded-lg w-11/12 max-w-2xl relative max-h-[90vh] overflow-y-auto">
                                        <h4 className="text-lg font-semibold mb-4 text-navy">
                                            {previewDoc.name}
                                        </h4>
                                        {[
                                            "jpg",
                                            "jpeg",
                                            "png",
                                            "gif",
                                            "webp",
                                        ].includes(previewDoc.ext) && (
                                            <img
                                                src={previewDoc.url}
                                                alt={previewDoc.name}
                                                className="max-w-full max-h-[70vh] mx-auto rounded shadow"
                                            />
                                        )}
                                        {["pdf"].includes(previewDoc.ext) && (
                                            <iframe
                                                src={previewDoc.url}
                                                className="w-full h-[70vh] rounded border"
                                                title="Document Preview"
                                            />
                                        )}
                                        {[
                                            "doc",
                                            "docx",
                                            "txt",
                                            "ppt",
                                            "pptx",
                                            "xls",
                                            "xlsx",
                                        ].includes(previewDoc.ext) && (
                                            <div className="text-center text-gray-600">
                                                <p>
                                                    No preview available for
                                                    this file type.
                                                </p>
                                                <a
                                                    href={previewDoc.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-blue-600 underline mt-2 inline-block"
                                                    download={previewDoc.name}
                                                >
                                                    Click here to download and
                                                    view
                                                </a>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setPreviewDoc(null)}
                                            className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Campaigns Section */}
                {activeTab === "campaigns" && (
                    <>
                        {campaigns.length === 0 ? (
                            <div>No approved campaigns found.</div>
                        ) : (
                            <>
                                {/* Drag and drop area for campaigns */}
                                <form
                                    onSubmit={(e) =>
                                        handleCampaignDocUpload(e, "global")
                                    }
                                    className="mb-6"
                                >
                                    <div className="mb-4">
                                        <label className="block mb-2 font-medium">
                                            Select Document
                                        </label>
                                        <div
                                            className="document-center-dropzone w-full border rounded p-4 text-center bg-gray-50 hover:bg-gray-100 transition mb-2 relative"
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                if(
                                                    e.dataTransfer.files &&
                                                    e.dataTransfer.files
                                                        .length > 0
                                                ) {
                                                    setCampaignDocFile(
                                                        (prev) => ({
                                                            ...prev,
                                                            global: e
                                                                .dataTransfer
                                                                .files[0],
                                                        }),
                                                    );
                                                }
                                            }}
                                            onDragOver={(e) =>
                                                e.preventDefault()
                                            }
                                            style={{
                                                border: "2px dashed #cbd5e1",
                                                position: "relative",
                                            }}
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        "doc-file-input-campaign",
                                                    )
                                                    ?.click()
                                            }
                                        >
                                            {campaignDocFile["global"] ? (
                                                <span className="flex items-center justify-center gap-2 text-navy font-semibold">
                                                    {
                                                        campaignDocFile[
                                                            "global"
                                                        ].name
                                                    }
                                                    <button
                                                        type="button"
                                                        className="ml-2 text-xs text-red-600 underline hover:text-red-800"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCampaignDocFile(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    global: null,
                                                                }),
                                                            );
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </span>
                                            ) : (
                                                <span className="text-gray-500">
                                                    Drag & drop a file here, or
                                                    click to select
                                                </span>
                                            )}
                                            <input
                                                id="doc-file-input-campaign"
                                                type="file"
                                                onChange={(e) =>
                                                    setCampaignDocFile(
                                                        (prev) => ({
                                                            ...prev,
                                                            global: e.target
                                                                .files[0],
                                                        }),
                                                    )
                                                }
                                                className="w-full opacity-0 absolute left-0 top-0 h-full cursor-pointer"
                                                style={{
                                                    position: "absolute",
                                                    pointerEvents: "none",
                                                }}
                                                tabIndex={-1}
                                            />
                                        </div>
                                    </div>
                                </form>
                                {/* Campaigns table controls (per page, search) */}
                                <div className="aqi-grid-toolbar">
                                        <div>
                                            <select
                                                style={{
                                                    padding:
                                                        "0.375rem 2rem 0.375rem 0.75rem",
                                                    color: "var(--navy)",
                                                    backgroundColor: "#F5F7FA",
                                                    border: "2px solid #C2D7E2",
                                                    borderRadius: "0.375rem",
                                                    fontWeight: "500",
                                                    appearance: "none",
                                                    backgroundImage:
                                                        "var(--navy-select-chevron)",
                                                    backgroundRepeat:
                                                        "no-repeat",
                                                    backgroundPosition:
                                                        "right 0.5rem center",
                                                    backgroundSize: "16px 12px",
                                                    transition: "all 0.2s ease",
                                                    cursor: "pointer",
                                                    outline: "none",
                                                }}
                                                className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                                value={campaignPerPage}
                                                onChange={(e) => {
                                                    setCampaignPerPage(
                                                        Number(e.target.value),
                                                    );
                                                    setCampaignCurrentPage(1);
                                                }}
                                            >
                                                {GRID_CONFIG.pageSizeOptions.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>

                                    <div className="aqi-grid-search">
                                        <FaSearch
                                            className="absolute left-3 top-2.5"
                                            style={{ color: "var(--navy)" }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Search by campaign..."
                                            value={campaignSearch}
                                            onChange={(e) => {
                                                setCampaignSearch(
                                                    e.target.value,
                                                );
                                                setCampaignCurrentPage(1);
                                            }}
                                            style={{
                                                paddingLeft: "2.5rem",
                                                paddingRight: "0.75rem",
                                                borderRadius: "0.375rem",
                                                border: "2px solid #C2D7E2",
                                                backgroundColor: "#F5F7FA",
                                                transition: "all 0.2s ease",
                                                outline: "none",
                                                color: "var(--navy)",
                                            }}
                                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm font-poppins"
                                        />
                                    </div>

                                    <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                                        <div className="relative group">
                                            <button
                                                type="button"
                                                onClick={(e) =>
                                                    handleCampaignDocUpload(
                                                        e,
                                                        "global",
                                                    )
                                                }
                                                className="aqi-grid-button aqi-grid-button--primary"
                                                disabled={
                                                    isCampaignUploadDisabled
                                                }
                                            >
                                                Upload
                                            </button>
                                            {isCampaignUploadDisabled && (
                                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity duration-200">
                                                    {campaignTooltip}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleExportCampaignsCSV}
                                            title="Export CSV"
                                            className="aqi-grid-button aqi-grid-button--primary"
                                        >
                                            Export
                                        </button>
                                    </div>
                                </div>
                                {/* Campaigns DataTable */}
                                <div
                                    style={{
                                        height: "56vh",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        borderRadius: "6px",
                                        width: "100%",
                                        display: "block",
                                    }}
                                >
                                    <DataTable
                                        columns={[
                                            {
                                                name: "Select",
                                                cell: (row) => (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(
                                                            row.id,
                                                        )}
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        onChange={() =>
                                                            handleUserSelect(
                                                                row.id,
                                                            )
                                                        }
                                                    />
                                                ),
                                                width: "80px",
                                                ignoreRowClick: true,
                                            },
                                            {
                                                name: "Campaign Name",
                                                selector: (row) =>
                                                    row.campaign_name,
                                                sortable: true,
                                                cell: (row) => (
                                                    <span className="font-semibold text-navy">
                                                        {row.campaign_name}
                                                    </span>
                                                ),
                                            },
                                            {
                                                name: "Company Name",
                                                selector: (row) =>
                                                    row.company_name,
                                                sortable: true,
                                            },
                                            {
                                                name: "Name",
                                                selector: (row) =>
                                                    `${row.first_name || ""} ${
                                                        row.last_name || ""
                                                    }`.trim(),
                                                sortable: true,
                                            },
                                            {
                                                name: "Email",
                                                selector: (row) =>
                                                    row.email ||
                                                    row.owner_email,
                                                sortable: true,
                                            },
                                        ]}
                                        data={paginatedCampaigns}
                                        customStyles={{
                                            ...customStyles,
                                            table: {
                                                style: {
                                                    borderRadius: "8px",
                                                    minWidth: 0,
                                                    width: "100%",
                                                    tableLayout: "fixed",
                                                    overflowX: "hidden",
                                                },
                                            },
                                            headRow: {
                                                style: {
                                                    backgroundColor: "var(--navy)",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    fontSize: "13px",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                },
                                            },
                                            rows: {
                                                style: {
                                                    cursor: "pointer",
                                                    minHeight: "50px",
                                                    width: "100%",
                                                    "&:hover": {
                                                        backgroundColor:
                                                            "#E9F4F9",
                                                        borderLeft:
                                                            "2px solid var(--navy)",
                                                    },
                                                },
                                            },
                                        }}
                                        onRowClicked={handleViewCampaignDocs}
                                        striped
                                        pointerOnHover
                                        noHeader
                                        highlightOnHover
                                    />
                                </div>
                                {/* Pagination for campaigns */}
                                <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                                    <span>
                                        Showing{" "}
                                        {filteredCampaigns.length === 0
                                            ? 0
                                            : (campaignCurrentPage - 1) *
                                                  campaignPerPage +
                                              1}{" "}
                                        to{" "}
                                        {Math.min(
                                            campaignCurrentPage *
                                                campaignPerPage,
                                            filteredCampaigns.length,
                                        )}{" "}
                                        of {filteredCampaigns.length} campaigns
                                    </span>
                                    <div className="flex gap-2 text-lg items-center">
                                        <button
                                            disabled={
                                                campaignCurrentPage === 1 ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage(1)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleLeft />
                                        </button>
                                        <button
                                            disabled={
                                                campaignCurrentPage === 1 ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage((prev) =>
                                                    Math.max(prev - 1, 1),)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleLeft />
                                        </button>
                                        <span className="text-sm px-2">
                                            Page {campaignCurrentPage} of{" "}
                                            {campaignTotalPages}
                                        </span>
                                        <button
                                            disabled={
                                                campaignCurrentPage ===
                                                    campaignTotalPages ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage((prev) =>
                                                    Math.min(
                                                        prev + 1,
                                                        campaignTotalPages,
                                                    ),)
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleRight />
                                        </button>
                                        <button
                                            disabled={
                                                campaignCurrentPage ===
                                                    campaignTotalPages ||
                                                loading
                                            }
                                            onClick={() =>
                                                setCampaignCurrentPage(
                                                    campaignTotalPages,
                                                )
                                            }
                                            className="disabled:opacity-40"
                                        >
                                            <FaAngleDoubleRight />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DocumentCenter;
